import React from "react";
import Button from "@mui/material/Button";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useTranslation } from "../i18n/LocaleContext";
import {
  MediaDeviceEntry,
  DeviceRealm,
  ChangeTrigger,
  shortId,
  deviceKey,
} from "../hooks/useMediaDeviceWatcher";

export type MediaDevicesState = {
  devices: MediaDeviceEntry[];
  /** Chaves dos dispositivos que entraram na última mudança — destacados até a próxima. */
  addedKeys: string[];
  anonymized: boolean;
  trigger: ChangeTrigger;
  enumeratedFrom: DeviceRealm;
  timestamp: string;
};

interface MediaDevicesPanelProps {
  state: MediaDevicesState | null;
  onRefresh: () => void;
  className?: string;
}

// Saída primeiro: é o dispositivo em investigação (onde toca o ring). Os nomes de kind ficam
// sem tradução — são o vocabulário da API, e o valor aqui é diagnóstico, não de produto.
const KIND_ORDER: MediaDeviceKind[] = ["audiooutput", "audioinput", "videoinput"];

const MediaDevicesPanel: React.FC<MediaDevicesPanelProps> = ({ state, onRefresh, className }) => {
  const { t } = useTranslation();
  const added = new Set(state?.addedKeys ?? []);

  return (
    <article className={`status-card devices-card${className ? ` ${className}` : ""}`}>
      <div className="card-header">
        <h2>{t("mediaDevicesPanel.title")}</h2>
        <Button variant="outlined" onClick={onRefresh} aria-label={t("mediaDevicesPanel.refreshAria")}>
          <RefreshIcon />
        </Button>
      </div>

      {!state && <p className="activity-empty-state">{t("mediaDevicesPanel.empty")}</p>}

      {state && (
        <div className="devices-list">
          {state.anonymized && (
            <p className="devices-warning">{t("mediaDevicesPanel.anonymized")}</p>
          )}

          {KIND_ORDER.map((kind) => {
            const ofKind = state.devices.filter((device) => device.kind === kind);
            if (ofKind.length === 0) return null;
            return (
              <section key={kind} className="devices-group">
                <h3>{kind}</h3>
                <ul>
                  {ofKind.map((device) => {
                    const key = deviceKey(device);
                    return (
                      <li key={key} className={added.has(key) ? "devices-item new" : "devices-item"}>
                        <span className="devices-label">
                          {device.label || t("mediaDevicesPanel.noLabel")}
                        </span>
                        <span className="devices-id">{shortId(device.deviceId)}</span>
                        {added.has(key) && <span className="devices-badge">{t("mediaDevicesPanel.new")}</span>}
                      </li>
                    );
                  })}
                </ul>
              </section>
            );
          })}

          {/* A procedência é o dado que responde por que a detecção só funciona em algumas
              plataformas: quais realms entregaram o evento e de qual saiu a lista. As três
              mensagens são separadas de propósito — reportar uma atualização manual como
              "evento" levaria à conclusão oposta da verdadeira onde nada é entregue. */}
          <p className="devices-provenance">
            {state.trigger.kind === "event" &&
              t("mediaDevicesPanel.provenanceEvent", {
                realms: state.trigger.realms.join(" + "),
                enumeratedFrom: state.enumeratedFrom,
                time: state.timestamp,
              })}
            {state.trigger.kind === "manual" &&
              t("mediaDevicesPanel.provenanceManual", {
                enumeratedFrom: state.enumeratedFrom,
                time: state.timestamp,
              })}
            {state.trigger.kind === "mount" &&
              t("mediaDevicesPanel.provenanceMount", {
                enumeratedFrom: state.enumeratedFrom,
                time: state.timestamp,
              })}
          </p>
        </div>
      )}
    </article>
  );
};

export default MediaDevicesPanel;
